/**
 * services/physical-media.ts — Physical-format POD for releases.
 *
 * A "pressing" attaches one or more physical-media SKUs (vinyl, cassette,
 * CD, DVD, Blu-ray) to an existing release in the `releases` collection.
 * Each pressing carries:
 *   • format            (vinyl|cassette|cd|dvd|bluray)
 *   • variants          (colour / runtime / sleeve etc.)
 *   • providerSlug      (qrates | diggers-factory | disc-republic | kunaki | duplication-ca)
 *   • prices            in cents per variant + a base price
 *   • run economics     minCopies (crowdfund threshold), targetCopies, currentCopies
 *   • status            draft | crowdfunding | production | shipping | complete | cancelled
 *
 * When a fan clicks "Buy on vinyl" on the release page we hit Stripe via the
 * existing checkout route with `type: 'physical-media'`. The webhook records
 * the order in `physicalMediaOrders/{sessionId}` and (where the provider has
 * an API) dispatches it via /api/pod/physical-dispatch.
 */

import { db } from "@/lib/firebase";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  setDoc,
  deleteDoc,
  where,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";

export type PhysicalFormat = "vinyl" | "cassette" | "cd" | "dvd" | "bluray";
export type PhysicalProviderSlug =
  | "qrates"
  | "diggers-factory"
  | "disc-republic"
  | "kunaki"
  | "duplication-ca";
export type PressingStatus =
  | "draft"            // metadata being filled in
  | "crowdfunding"     // public pre-orders accumulating toward minCopies
  | "production"       // threshold hit, pressing in progress
  | "shipping"         // first copies dispatched
  | "complete"         // all known orders fulfilled
  | "cancelled";       // pressing aborted

/** A single buyable variant of a pressing — e.g. "180g red vinyl 12\"". */
export interface PhysicalVariant {
  id: string;                   // stable id within the pressing
  label: string;                // "Red 180g, gatefold"
  colour?: string;              // for vinyl/cassette body colour
  weight?: string;              // 180g, 140g
  speed?: string;               // 33 1/3 RPM, 45 RPM
  size?: string;                // 7", 10", 12"
  sleeve?: string;              // gatefold, standard, deluxe digipak
  /** Per-variant price overrides the pressing base price when set. */
  priceCents?: number;
  /** Available stock cap on this variant (0/undef = unlimited). */
  stock?: number;
  /** Fulfilled count — incremented by the webhook for accounting. */
  sold?: number;
}

export interface Pressing {
  id: string;
  releaseId: string;            // doc id in `releases`
  releaseTitle?: string;        // denormalised for list views
  artistName?: string;
  coverArtUrl?: string;

  format: PhysicalFormat;
  variants: PhysicalVariant[];
  /** Default price when a variant doesn't set its own. */
  basePriceCents: number;
  currency: string;             // "EUR" | "SEK" | "USD" | "GBP"

  providerSlug: PhysicalProviderSlug;
  /** Crowdfunding threshold — at 0 the pressing is on-demand from the start. */
  minCopies: number;
  /** Aspirational ceiling, surfaced on the product page. */
  targetCopies?: number;
  /** Sum of paid + crowdfund-authorised orders. Webhook updates. */
  currentCopies: number;

  /** Free-form notes for the manufacturer (matte vs gloss, OBI strip, etc.). */
  manufacturerNotes?: string;
  /** Lead time, weeks, displayed to buyers under "Ships in ~N weeks". */
  leadTimeWeeks?: number;

  status: PressingStatus;
  /** Crowdfund deadline — the pressing auto-cancels and refunds if not hit. */
  crowdfundDeadline?: Timestamp;
  /** Publisher / artist owner — Stripe Connect destination. */
  ownerUid?: string;

  createdAt?: Timestamp;
  updatedAt?: Timestamp;
}

const COL = "pressings";

const toPressing = (snap: any): Pressing => ({ id: snap.id, ...(snap.data() as Omit<Pressing, "id">) });

export async function listPressings(opts: {
  releaseId?: string;
  status?: PressingStatus;
  format?: PhysicalFormat;
  ownerUid?: string;
  max?: number;
} = {}): Promise<Pressing[]> {
  const cs: any[] = [];
  if (opts.releaseId) cs.push(where("releaseId", "==", opts.releaseId));
  if (opts.status)    cs.push(where("status", "==", opts.status));
  if (opts.format)    cs.push(where("format", "==", opts.format));
  if (opts.ownerUid)  cs.push(where("ownerUid", "==", opts.ownerUid));
  cs.push(orderBy("updatedAt", "desc"));
  const q = query(collection(db, COL), ...cs);
  const snap = await getDocs(q);
  let all = snap.docs.map(toPressing);
  if (opts.max) all = all.slice(0, opts.max);
  return all;
}

export async function getPressingById(id: string): Promise<Pressing | null> {
  const snap = await getDoc(doc(db, COL, id));
  if (!snap.exists()) return null;
  return toPressing(snap);
}

export async function savePressing(input: Partial<Pressing> & { id?: string; releaseId: string; format: PhysicalFormat }): Promise<string> {
  const id = input.id || doc(collection(db, COL)).id;
  const payload: any = { ...input, id, updatedAt: serverTimestamp() };
  const ref = doc(db, COL, id);
  const existing = await getDoc(ref);
  if (!existing.exists()) payload.createdAt = serverTimestamp();
  await setDoc(ref, payload, { merge: true });
  return id;
}

export async function deletePressing(id: string): Promise<void> {
  await deleteDoc(doc(db, COL, id));
}

// ─── Provider catalogue (UI helper) ──────────────────────────────────────────

/** Best provider per format — the default picked when a creator adds a pressing. */
export const DEFAULT_PROVIDER_BY_FORMAT: Record<PhysicalFormat, PhysicalProviderSlug> = {
  vinyl:    "qrates",            // crowdfunding pressing model, eco-friendly
  cassette: "qrates",            // also presses cassettes via the same flow
  cd:       "kunaki",            // ultra-low cost true-on-demand for CDs
  dvd:      "kunaki",            // same
  bluray:   "kunaki",            // same
};

/** Human label per format for UI strings. */
export const FORMAT_LABELS: Record<PhysicalFormat, string> = {
  vinyl:    "Vinyl",
  cassette: "Cassette",
  cd:       "CD",
  dvd:      "DVD",
  bluray:   "Blu-ray",
};
